import { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useAPI, useUpdatePageTitle, createTitleFromSegments } from "@humansignal/core";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { Button, SettingsCard, useToast } from "@humansignal/ui";
import { cn } from "../../utils/bem";
import { ProjectContext, ProjectProvider } from "../../providers/ProjectProvider";
import { useContext } from "react";
import { ProjectNav } from "../ProjectDashboard/ProjectNav";
import { ConsentGate } from "../../components/ConsentGate/ConsentGate";
import { TabHeader } from "../../components/TabHeader/TabHeader";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { ToggleItems } from "../../components/ToggleItems/ToggleItems";
import { useTranslation } from "../../i18n/LocaleProvider";
import { ProjectLocaleScope } from "../../i18n/ProjectLocaleScope";
import "./ReviewStream.prefix.css";

const block = cn("review-stream");

// Minimal preview of an annotation's ``result`` array. A full re-use
// of the Label Studio editor would be too heavy for this first cut —
// the reviewer gets the raw task data + a list of the labels / values
// the annotator applied, and can Accept or Reject. ``Fix & Accept``
// requires the editor; we surface the button but route it to the
// Data Manager's single-task view where the annotator-grade editor
// already lives.
// Stable signature of an annotation's ``result`` so client-side can
// cluster annotations that share the same labelling. Mirrors the
// backend's ``_result_signature`` in ``projects/review_api.py`` so
// that filtering decisions stay consistent across both sides.
const resultSignature = (result) => {
  if (!Array.isArray(result)) return "";
  const parts = [];
  for (const item of result) {
    if (!item || typeof item !== "object") continue;
    const fromName = item.from_name ?? "";
    const value = item.value ?? {};
    if (!value || typeof value !== "object") continue;
    for (const key of Object.keys(value).sort()) {
      const v = value[key];
      const norm = Array.isArray(v) ? v.map(String).sort().join(",") : String(v);
      parts.push(`${fromName}:${key}=${norm}`);
    }
  }
  return parts.sort().join("|");
};

const ResultPreview = ({ result }) => {
  const { t } = useTranslation();
  if (!Array.isArray(result) || result.length === 0) {
    return (
      <div className={block.elem("empty").toClassName()}>{t("pages.review.review_stream.annotation_is_empty")}</div>
    );
  }
  return (
    <ul className={block.elem("result-list").toClassName()}>
      {result.map((item, i) => {
        const tag = item?.from_name ?? "?";
        const type = item?.type ?? "?";
        const value = item?.value ?? {};
        const pieces = Object.entries(value)
          .filter(([, v]) => !Array.isArray(v) || v.length > 0)
          .map(([k, v]) => ({ k, v: Array.isArray(v) ? v.join(", ") : String(v ?? "") }));
        return (
          <li key={i} className={block.elem("result-item").toClassName()}>
            <div className={block.elem("result-head").toClassName()}>
              <span className={block.elem("result-tag").toClassName()}>{tag}</span>
              <span className={block.elem("result-type").toClassName()}>{type}</span>
            </div>
            {pieces.length === 0 ? (
              <span className={block.elem("result-empty").toClassName()}>
                {t("pages.review.review_stream.no_value")}
              </span>
            ) : (
              <dl className={block.elem("result-dl").toClassName()}>
                {pieces.map(({ k, v }) => (
                  <div key={k} className={block.elem("result-row").toClassName()}>
                    <dt>{k}</dt>
                    <dd>{v}</dd>
                  </div>
                ))}
              </dl>
            )}
          </li>
        );
      })}
    </ul>
  );
};

const TaskDataPreview = ({ data }) => {
  if (!data || typeof data !== "object") return null;
  return (
    <dl className={block.elem("task-data").toClassName()}>
      {Object.entries(data).map(([k, v]) => (
        <div key={k} className={block.elem("task-row").toClassName()}>
          <dt>{k}</dt>
          <dd>
            {typeof v === "string" ? (
              /^https?:\/\/\S+\.(png|jpe?g|gif|webp|svg)$/i.test(v) ? (
                <img src={v} alt={k} className={block.elem("task-image").toClassName()} />
              ) : (
                v
              )
            ) : (
              <pre>{JSON.stringify(v, null, 2)}</pre>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
};

const Inner = () => {
  const { t } = useTranslation();
  const api = useAPI();
  const toast = useToast();
  const history = useHistory();
  const { project } = useContext(ProjectContext);
  const projectId = project?.id;

  useUpdatePageTitle(createTitleFromSegments([project?.title, "Review"]));

  const [payload, setPayload] = useState(null); // { done, task, annotations, review_settings, total? }
  const [loading, setLoading] = useState(false);
  // "to_review" = forward stream of unreviewed items; "reviewed" =
  // revisit items the caller has already reviewed (prev/next nav).
  const [mode, setMode] = useState("to_review");
  const [offset, setOffset] = useState(0); // position in the reviewed list
  // Per-annotation submit state: map of annotationId → "accept" | "reject" | ...
  const [submitting, setSubmitting] = useState({});
  // Per-annotation verdict state tracked locally so a reviewer can
  // act on each annotation independently and see the card fade / show
  // the verdict without re-fetching the whole task.
  const [verdicts, setVerdicts] = useState({}); // {id: "accepted" | "fixed" | "rejected"}
  // Per-group "change verdict" state — re-opens the action buttons on a
  // group that already carries a stored verdict (revisit flow).
  const [changing, setChanging] = useState({}); // {keyId: true}
  // Per-annotation reject-mode (comment UI open) state.
  const [rejectMode, setRejectMode] = useState({});
  const [rejectComments, setRejectComments] = useState({});

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setRejectMode({});
    setRejectComments({});
    setVerdicts({});
    setChanging({});
    const res =
      mode === "reviewed"
        ? await api.callApi("projectReviewStreamReviewed", {
            params: { pk: projectId, offset },
            suppressError: true,
          })
        : await api.callApi("projectReviewStreamNext", {
            params: { pk: projectId },
            suppressError: true,
          });
    setLoading(false);
    if (!res || res.error) {
      toast.show({ message: res?.error ?? "Could not load task", type: "error" });
      setPayload({ done: true, task: null, annotations: [] });
      return;
    }
    setPayload(res);
  }, [api, projectId, toast, mode, offset]);

  useEffect(() => {
    load();
  }, [load]);

  const switchMode = useCallback(
    (next) => {
      if (next === mode) return;
      setOffset(0);
      setMode(next);
    },
    [mode],
  );

  const requireCommentOnReject = !!payload?.review_settings?.require_comment_on_reject;

  // Apply an action to every annotation in a group. Submits serially
  // so partial failures surface to the user (the toast below reports
  // just the first error). Keys for submitting/verdicts/rejectMode are
  // per-group (the first annotation's id) so the UI can track one
  // card per group.
  const submitGroup = useCallback(
    async (group, action) => {
      if (!group || group.length === 0) return;
      const keyId = group[0].id;
      const comment = (rejectComments[keyId] || "").trim();
      if (action === "reject" && requireCommentOnReject && !comment) {
        setRejectMode((s) => ({ ...s, [keyId]: true }));
        return;
      }
      setSubmitting((s) => ({ ...s, [keyId]: action }));

      let anyFailure = null;
      const newVerdicts = {};
      for (const a of group) {
        const res = await api.callApi("annotationReview", {
          params: { pk: a.id },
          body: { action, comment: action === "reject" ? comment : "" },
          suppressError: true,
        });
        if (!res || res.error) {
          anyFailure = res?.error ?? "Review failed";
          break;
        }
        newVerdicts[a.id] = res.review_state;
      }

      setSubmitting((s) => {
        const next = { ...s };
        delete next[keyId];
        return next;
      });

      if (anyFailure) {
        toast.show({ message: anyFailure, type: "error" });
        // Still apply the verdicts we did land so the UI doesn't lie.
        if (Object.keys(newVerdicts).length) setVerdicts((v) => ({ ...v, ...newVerdicts }));
        return;
      }

      const label =
        action === "accept"
          ? `Accepted ${group.length > 1 ? `${group.length} annotations` : ""}`.trim()
          : action === "fix_accept"
            ? "Marked as fixed & accepted"
            : `Rejected ${group.length > 1 ? `${group.length} annotations` : ""}`.trim();
      toast.show({ message: label });
      setVerdicts((v) => ({ ...v, ...newVerdicts }));
      setRejectMode((s) => ({ ...s, [keyId]: false }));
      setChanging((s) => ({ ...s, [keyId]: false }));

      // In the revisit ("reviewed") stream we stay on the current item so
      // the reviewer can see the corrected verdict — no forward advance.
      if (mode === "reviewed") return;

      // Advance if every annotation in the task has a verdict now.
      const remaining = (payload?.annotations ?? []).filter(
        (a) => !(a.id in newVerdicts) && !verdicts[a.id] && !a.review_state,
      );
      if (remaining.length === 0) load();
    },
    [api, payload, rejectComments, requireCommentOnReject, toast, load, verdicts, mode],
  );

  // Returning from the Fix round-trip (or any tab resume): refetch the
  // current item so the verdict recorded server-side replaces the
  // stale three-button state — the exact confusion reviewers hit.
  useEffect(() => {
    const onFocus = () => {
      if (!document.hidden) load();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [load]);

  const openInEditor = (annotationId) => {
    // Fix & Accept needs the full annotation editor — route to the
    // Data Manager's single-task view where it already lives. Annotation
    // id is passed via hash so the DM focuses the right row; the review
    // stream picks up the next task on return.
    const tid = payload?.task?.id;
    if (!tid || !projectId) return;
    // NOT ``labeling=1``: that flag starts the label STREAM, which
    // picks the next task for the reviewer instead of this one (and
    // opens a fresh annotation) — the "wrong task, no annotations" bug.
    // ``task`` + ``annotation`` open exactly this annotation in the
    // editor; ``review=1`` marks the round-trip.
    history.push(`/projects/${projectId}/data?task=${tid}&review=1&annotation=${annotationId}`);
  };

  const isDone = payload?.done;
  const task = payload?.task;
  const annotations = payload?.annotations ?? [];

  // Cluster annotations by their ``result`` signature so identical
  // labellings share one card + one verdict row. Preserves
  // chronological order within a cluster. If everyone agreed you
  // see a single card; if two of three agreed you see two cards
  // (one with the pair, one with the dissenter).
  const groups = [];
  {
    const bySig = new Map();
    for (const a of annotations) {
      const sig = resultSignature(a.result);
      if (!bySig.has(sig)) {
        const g = [];
        bySig.set(sig, g);
        groups.push(g);
      }
      bySig.get(sig).push(a);
    }
  }

  const conflictPolicy = payload?.review_settings?.conflict_policy;
  const ACCEPTED_STATES = new Set(["accepted", "fixed"]);
  const groupIsAccepted = (g) => ACCEPTED_STATES.has(verdicts[g[0].id] ?? g[0].review_state);
  // Warn-on-conflict: under 'warn', accepting a second annotation whose
  // result differs from one already accepted on this task needs a
  // confirm. Groups are clustered by result signature, so any OTHER
  // accepted group is by definition a different result.
  const wouldConflict = (group) => conflictPolicy === "warn" && groups.some((g) => g !== group && groupIsAccepted(g));

  const reviewedMode = mode === "reviewed";
  const total = payload?.total ?? 0;

  return (
    <ProjectLocaleScope project={project}>
      <div className={block.toClassName()}>
        {/* Shared TabHeader so the review-stream page reads as a sibling
          of other project-level surfaces (Dashboard / Analytics /
          Settings) — same H2 + subtitle + actions rhythm instead of a
          bespoke title block. */}
        <TabHeader
          title={project?.title ? `${project.title}. Review` : "Review"}
          subtitle={t("pages.review.review_stream.accept_or_reject_annotator_work_fix_acce")}
          actions={
            <div className={block.elem("header-actions").toClassName()}>
              <ToggleItems
                className={block.elem("mode-toggle").toClassName()}
                items={{ to_review: "To review", reviewed: "Reviewed" }}
                active={mode}
                onSelect={switchMode}
              />
              <Button size="small" look="outlined" onClick={() => history.goBack()}>
                Close
              </Button>
            </div>
          }
        />

        {loading && (
          <div className={block.elem("status").toClassName()}>{t("pages.review.review_stream.loading_next_task")}</div>
        )}

        {!loading && isDone && reviewedMode && (
          <EmptyState
            title="Nothing reviewed yet"
            description="Items you accept, fix, or reject will show up here so you can revisit them."
          />
        )}

        {!loading && isDone && !reviewedMode && (
          <EmptyState
            title={t("pages.review.review_stream.queue_clear")}
            description={t("pages.review.review_stream.there_are_no_more_annotations_waiting_fo")}
            action={
              <Button size="small" onClick={() => history.push(`/projects/${projectId}/data`)}>
                Back to Data Manager
              </Button>
            }
          />
        )}

        {!loading && !isDone && task && annotations.length > 0 && (
          <div className={block.elem("body").toClassName()}>
            {reviewedMode && (
              <div className={block.elem("nav").toClassName()}>
                <Button
                  size="small"
                  look="outlined"
                  onClick={() => setOffset((o) => Math.max(0, o - 1))}
                  disabled={offset <= 0}
                >
                  ← Previous
                </Button>
                <span className={block.elem("nav-count").toClassName()}>
                  {offset + 1} of {total} reviewed
                </span>
                <Button
                  size="small"
                  look="outlined"
                  onClick={() => setOffset((o) => Math.min(total - 1, o + 1))}
                  disabled={offset >= total - 1}
                >
                  Next →
                </Button>
              </div>
            )}
            <SettingsCard title={`Task #${task.id}`}>
              <TaskDataPreview data={task.data} />
            </SettingsCard>

            {/* One card per group of annotations that produced the same
              result. A group of two shows a single verdict row and
              applies Accept / Reject to both annotations at once —
              avoids the reviewer clicking twice on identical work. */}
            <div className={block.elem("annotations-grid").toClassName()}>
              {groups.map((group) => {
                const first = group[0];
                const keyId = first.id;
                const grouped = group.length > 1;
                // Group-level verdict — when a prior session already
                // reviewed some of the members we surface that, but
                // local in-session verdicts take priority.
                const groupVerdict = verdicts[keyId] ?? first.review_state;
                const rmode = !!rejectMode[keyId];
                const busy = submitting[keyId];
                // "Change verdict" re-opens the action buttons on a group
                // that already carries a stored verdict.
                const editing = !!changing[keyId];
                // Disputed items arrive already-rejected: open the verdict
                // buttons directly instead of hiding them behind
                // "Change verdict" — the whole point is a fresh judgment.
                const showActions = !groupVerdict || editing || (!!first.dispute_open && !verdicts[keyId]);

                const headerTitle = grouped ? `${group.length} matching annotations` : `Annotation #${first.id}`;
                const headerDesc = grouped
                  ? `Annotators: ${group
                      .map((a) => a.completed_by_email ?? `user ${a.completed_by ?? "?"}`)
                      .join(", ")}`
                  : `${first.completed_by_email ?? `user ${first.completed_by ?? "?"}`}, ` +
                    new Date(first.created_at).toLocaleString();

                return (
                  <SettingsCard key={keyId} title={headerTitle} description={headerDesc}>
                    {/* Open rejection dispute — the annotator asked for a
                      re-review; their reason leads the card so the
                      reviewer reads it before re-judging. */}
                    {first.dispute_open && (
                      <div className={block.elem("dispute-banner").toClassName()}>
                        <strong>Re-review requested.</strong>{" "}
                        {first.dispute_reason ? (
                          <>Annotator's reason: “{first.dispute_reason}”</>
                        ) : (
                          "The annotator asked for another look at this rejection."
                        )}
                      </div>
                    )}
                    {grouped && (
                      <div className={block.elem("group-badge").toClassName()}>
                        Consensus, same labelling across {group.length} annotators
                      </div>
                    )}

                    {groupVerdict && (
                      <div className={block.elem("verdict-row").toClassName()}>
                        <div
                          className={block
                            .elem("verdict-chip")
                            .mod({ [groupVerdict]: true })
                            .toClassName()}
                        >
                          {groupVerdict === "accepted"
                            ? "Accepted"
                            : groupVerdict === "fixed"
                              ? "Fixed & Accepted"
                              : "Rejected"}
                        </div>
                        {first.review_diff && (
                          <div className={block.elem("fix-note").toClassName()}>
                            <span className={block.elem("fix-note-head").toClassName()}>
                              Fixed{first.reviewed_by_email ? ` by ${first.reviewed_by_email}` : ""}:{" "}
                              {first.review_diff.summary}
                            </span>
                            <span className={block.elem("fix-chips").toClassName()}>
                              {(first.review_diff.changed || []).slice(0, 6).map((c, i) => (
                                <span key={`c${i}`} className={block.elem("fix-chip").toClassName()}>
                                  {spanLabel(c.before)}
                                  {" → "}
                                  {spanLabel(c.after)}
                                </span>
                              ))}
                              {(first.review_diff.added || []).slice(0, 4).map((c, i) => (
                                <span key={`a${i}`} className={block.elem("fix-chip").mod({ add: true }).toClassName()}>
                                  + {spanLabel(c)}
                                </span>
                              ))}
                              {(first.review_diff.removed || []).slice(0, 4).map((c, i) => (
                                <span key={`r${i}`} className={block.elem("fix-chip").mod({ del: true }).toClassName()}>
                                  − {spanLabel(c)}
                                </span>
                              ))}
                            </span>
                          </div>
                        )}
                        {!editing && (
                          <Button
                            variant="neutral"
                            look="string"
                            size="small"
                            onClick={() => {
                              setChanging((s) => ({ ...s, [keyId]: true }));
                              setRejectMode((s) => ({ ...s, [keyId]: false }));
                            }}
                            disabled={!!busy}
                          >
                            Change verdict
                          </Button>
                        )}
                        {!editing && groupVerdict !== "rejected" && (
                          <Button
                            variant="neutral"
                            look="string"
                            size="small"
                            onClick={() => openInEditor(first.id)}
                            disabled={!!busy}
                            title="Re-open the editor to amend the fix — the annotator's original stays snapshotted"
                          >
                            Amend fix
                          </Button>
                        )}
                      </div>
                    )}

                    <ResultPreview result={first.result} />

                    {rmode && showActions && (
                      <>
                        <textarea
                          className={block.elem("comment").toClassName()}
                          placeholder={t("pages.review.review_stream.why_is_this_annotation_being_rejected_th")}
                          value={rejectComments[keyId] ?? ""}
                          onChange={(e) => setRejectComments((s) => ({ ...s, [keyId]: e.target.value }))}
                          rows={3}
                        />
                        <div className={block.elem("actions").toClassName()}>
                          <Button
                            variant="neutral"
                            look="outlined"
                            size="small"
                            onClick={() => setRejectMode((s) => ({ ...s, [keyId]: false }))}
                            disabled={!!busy}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="negative"
                            size="small"
                            onClick={() => submitGroup(group, "reject")}
                            disabled={!!busy || (requireCommentOnReject && !(rejectComments[keyId] || "").trim())}
                          >
                            {busy === "reject" ? "Rejecting…" : "Submit rejection"}
                          </Button>
                        </div>
                      </>
                    )}

                    {!rmode && showActions && (
                      <div className={block.elem("verdict").toClassName()}>
                        <Button
                          variant="negative"
                          look="outlined"
                          size="small"
                          onClick={() => {
                            if (requireCommentOnReject) {
                              setRejectMode((s) => ({ ...s, [keyId]: true }));
                              return;
                            }
                            submitGroup(group, "reject");
                          }}
                          disabled={!!busy}
                        >
                          {busy === "reject" ? "Rejecting…" : "Reject"}
                        </Button>
                        <Button
                          variant="neutral"
                          look="outlined"
                          size="small"
                          onClick={() => openInEditor(first.id)}
                          disabled={!!busy}
                          title={t("pages.review.review_stream.open_the_annotation_editor_to_make_corre")}
                        >
                          Fix &amp; Accept
                        </Button>
                        <Button
                          variant="positive"
                          size="small"
                          onClick={() => {
                            if (
                              wouldConflict(group) &&
                              // eslint-disable-next-line no-alert
                              !window.confirm("This task already has a different accepted annotation, accept this too?")
                            ) {
                              return;
                            }
                            submitGroup(group, "accept");
                          }}
                          disabled={!!busy}
                        >
                          {busy === "accept" ? "Accepting…" : "Accept"}
                        </Button>
                        {editing && (
                          <Button
                            variant="neutral"
                            look="string"
                            size="small"
                            onClick={() => setChanging((s) => ({ ...s, [keyId]: false }))}
                            disabled={!!busy}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    )}
                  </SettingsCard>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </ProjectLocaleScope>
  );
};

/** Chip label for a diff entry: labels/text plus WHERE — character
 *  offsets for text spans, m:ss for audio/video timestamps. */
const spanLabel = (b) => {
  const what = (b.labels || [b.text || b.type]).join("/");
  if (!b.span) return what;
  const [s0, s1] = b.span;
  if (typeof s0 === "number" && (s0 > 300 || !Number.isInteger(s0))) {
    const mmss = (x) => `${Math.floor(x / 60)}:${String(Math.floor(x % 60)).padStart(2, "0")}`;
    return `${what} (${mmss(s0)}-${mmss(s1)})`;
  }
  return `${what} (${s0}-${s1})`;
};

export const ReviewStreamPage = () => {
  const { id } = useParams();
  return (
    <ProjectProvider>
      <ConsentGate projectId={id}>
        <Inner />
      </ConsentGate>
    </ProjectProvider>
  );
};

ReviewStreamPage.title = "Review";
ReviewStreamPage.path = "/review-stream";
ReviewStreamPage.exact = true;

// Mount the shared project nav while the review stream is open so the
// operator can flip back to Dashboard / Data / Settings without
// leaving the project context.
ReviewStreamPage.context = () => {
  const { project } = useContext(ProjectContext);
  const { user } = useAuth();
  if (!project?.id) return null;
  const role = user?.active_organization_role;
  const canManage =
    !!user &&
    (user.is_staff || user.is_superuser || ["OW", "AD", "MA"].includes(role) || project?.created_by?.id === user.id);
  return <ProjectNav projectId={project.id} canManage={canManage} project={project} userRole={role} />;
};
